/// <amd-module name="Wit/DirectoryService/IDirectoryService" />
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IListBoxItem } from "VSSUI/ListBox";
import { Filter, IFilter, IFilterState } from "VSSUI/Utilities/Filter";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { DirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { IDirectoryDropdownItem, IDirectoryTreeItem } from "Wit/DirectoryService/Models";

export namespace GroupIds {
    export const All = "all";
    export const Favorites = "favorites";
    export const Team = "team";
    export const Recent = "recent";
}

export interface IDirectoryService<T extends DirectoryItem = DirectoryItem> extends IVssService {
    getDirectoryDropdownData(): Promise<IDirectoryDropdownItem[]>;
    getDirectoryFilter(): IFilter;
    isFiltering: IReadonlyObservableValue<boolean>;
    getTeamsItemProvider(): IReadonlyObservableArray<IListBoxItem>;
    getDirectoryTreeItemProvider(): TreeItemProvider<IDirectoryTreeItem<T>>;
    toggleTreeItem(treeItem: ITreeItemEx<IDirectoryTreeItem<T>>): void;
    addFavorite(id: string): void;
    removeFavorite(id: string): void;
}

export abstract class DirectoryService<T extends DirectoryItem = DirectoryItem> extends VssService implements IDirectoryService {
    private allDirectoryData: T[] = [];
    private continueDirectoryData: T[] = [];
    private favoritesDirectoryData: T[] = [];
    private readonly filter = new Filter();
    private placeholderRowCount = 5;
    private readonly filteredAllDirectoryData = new ObservableArray<T | undefined>(new Array(this.placeholderRowCount).fill(undefined));
    private isFilteredObservable = new ObservableValue<boolean>(false);
    private getDirectoryDropdownDataPromise?: Promise<IDirectoryDropdownItem[]>;
    private initialDirectories: { [key: string]: T } = {}; // directories that show up on initial load
    private loadAllDirectoryDataPromise?: Promise<T[]>;
    private loadDirectoryDataPromise?: Promise<void>;
    private teamDirectoryData: T[] = [];
    private teamItemProvider: ObservableArray<IListBoxItem>;
    private treeItemProvider: TreeItemProvider<IDirectoryTreeItem<T>>;

    protected abstract myDirectoryDataProviderContribution: string;
    protected abstract allDirectoryDataProviderContribution: string;

    public get isFiltering(): IReadonlyObservableValue<boolean> {
        return this.isFilteredObservable;
    }

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
        this.filteredAllDirectoryData.subscribe(this.updateTreeItemProviderItems);
        this.filter.subscribe(this.filterAllDirectoryData);
        this.filter.subscribe(this.updateIsFiltering);
    };

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
        this.filteredAllDirectoryData.unsubscribe(this.updateTreeItemProviderItems);
        this.filter.unsubscribe(this.filterAllDirectoryData);
        this.filter.unsubscribe(this.updateIsFiltering);
    };

    public getDirectoryDropdownData = (): Promise<IDirectoryDropdownItem[]> => {
        if (!this.getDirectoryDropdownDataPromise) {
            this.getDirectoryDropdownDataPromise = Promise.all([this.loadAllDirectoryData(), this.loadDirectoryData()]).then(() => {
                const seen: { [key: string]: boolean } = {};
                const items: IDirectoryDropdownItem[] = [];
                const processItem = (item: T, group: string) => {
                    if (!seen[item.id]) {
                        items.push(createDirectoryDropdownItem(item, group));
                        seen[item.id] = true;
                    }
                };
                this.favoritesDirectoryData.forEach(item => processItem(item, GroupIds.Favorites));
                this.teamDirectoryData.forEach(item => processItem(item, GroupIds.Team));
                this.allDirectoryData.forEach(item => processItem(item, GroupIds.All));
                return items;
            });
        }

        return this.getDirectoryDropdownDataPromise;
    };

    public getDirectoryFilter = (): IFilter => {
        return this.filter;
    };

    public getDirectoryTreeItemProvider = () => {
        if (!this.treeItemProvider) {
            this.treeItemProvider = new TreeItemProvider<IDirectoryTreeItem<T>>([]);
            this.loadDirectoryData();
            this.updateTreeItemProviderItems();
        }

        return this.treeItemProvider;
    };

    public getTeamsItemProvider = (): ObservableArray<IListBoxItem> => {
        if (!this.teamItemProvider) {
            const loadTeamItems = async () => {
                await this.loadAllDirectoryData();
                this.teamItemProvider.value = this.allDirectoryData.map(directory => {
                    return {
                        id: directory.id,
                        text: directory.name
                    };
                });
            };

            this.teamItemProvider = new ObservableArray<IListBoxItem>([]);
            loadTeamItems();
        }

        return this.teamItemProvider;
    };

    public toggleTreeItem = (treeItem: ITreeItemEx<IDirectoryTreeItem<T>>) => {
        let item = treeItem.underlyingItem;
        if (item.id === GroupIds.All) {
            this.loadAllDirectoryData();
        }

        this.treeItemProvider.toggle(item);
    };

    public addFavorite = (id: string) => {
        const favIndex = this.favoritesDirectoryData.findIndex(item => item.id === id);

        if (favIndex === -1) {
            const item = this.initialDirectories[id] || this.allDirectoryData?.find(item => item.id === id);

            if (item) {
                this.favoritesDirectoryData.push(item);
                this.favoritesDirectoryData = this.favoritesDirectoryData.sort(directoryCompare);
            }
        }
    };

    public removeFavorite = (id: string) => {
        const index = this.favoritesDirectoryData.findIndex(item => item.id === id);
        if (index !== -1) {
            this.favoritesDirectoryData.splice(index, 1);
        }
    };

    private filterAllDirectoryData = () => {
        if (this.allDirectoryData.length === 0) {
            this.updateTreeItemProviderItems();
        }

        this.loadAllDirectoryData().then(() => {
            this.filteredAllDirectoryData.value = filterDirectories(this.allDirectoryData, this.filter.getState());
        });
    };

    private updateTreeItemProviderItems = () => {
        if (this.treeItemProvider) {
            let treeItems: ITreeItem<IDirectoryTreeItem<T>>[] = [];

            if (hasFilter(this.filter.getState())) {
                treeItems = treeItems.concat(this.flattenDirectoryData(this.filteredAllDirectoryData));
            } else {
                if (this.continueDirectoryData.length > 0) {
                    treeItems = treeItems.concat(this.convertDirectoryDataToTreeItems(this.continueDirectoryData, GroupIds.Recent));
                }

                treeItems = treeItems
                    .concat(this.convertDirectoryDataToTreeItems(this.favoritesDirectoryData, GroupIds.Favorites))
                    .concat(this.convertDirectoryDataToTreeItems(this.teamDirectoryData, GroupIds.Team))
                    .concat(this.convertDirectoryDataToTreeItems(this.filteredAllDirectoryData.value, GroupIds.All));
            }

            this.treeItemProvider.splice(undefined, this.treeItemProvider.roots, [{ items: treeItems }]);
        }
    };

    private convertDirectoryDataToTreeItem = (directoryItem: T | undefined) => {
        return {
            data: directoryItem ? { directoryItem: { ...directoryItem }, isFolder: false, linkProps: { href: directoryItem.url } } : undefined
        } as ITreeItem<IDirectoryTreeItem<T>>;
    };

    private flattenDirectoryData = (directoryItems: ObservableArray<T | undefined>) => {
        return directoryItems.value.map(this.convertDirectoryDataToTreeItem);
    };

    private convertDirectoryDataToTreeItems = (directoryItems: (T | undefined)[], groupId: string) => {
        const treeItems = [
            {
                childItems: directoryItems.map(this.convertDirectoryDataToTreeItem),
                data: {
                    directoryItem: { id: groupId },
                    isFolder: true
                },
                expanded: this.isExpanded(groupId),
                id: groupId
            } as ITreeItem<IDirectoryTreeItem<T>>
        ];

        return treeItems;
    };

    private showErrorMessage = (error: any) => {
        // todo
    };

    /**
     * Get the current expanded state for a group, defaulting to true unless it's the all group
     */
    private isExpanded = (id: string): boolean => {
        const expanded = this.treeItemProvider.roots.find(root => root.id === id)?.expanded;
        return expanded === undefined ? id !== GroupIds.All : expanded;
    };

    private loadDirectoryData = (): Promise<void> => {
        if (!this.loadDirectoryDataPromise) {
            this.loadDirectoryDataPromise = new Promise((resolve, reject) => this.loadDirectoryDataHelper(resolve, reject));
        }
        return this.loadDirectoryDataPromise;
    };

    private loadDirectoryDataHelper = (resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: any) => void) => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        const data = contributionService.getData<T[]>(this.myDirectoryDataProviderContribution);

        const processData = (data: T[] | undefined) => {
            if (data) {
                data.forEach(dir => {
                    if (dir.isMostRecent) {
                        this.continueDirectoryData.push(dir);
                    }

                    if (dir.isFavorite) {
                        this.favoritesDirectoryData.push(dir);
                    }

                    if (dir.isMember) {
                        this.teamDirectoryData.push(dir);
                    }
                });
            }

            resolve();
        };
        if (data) {
            processData(data);
        } else {
            contributionService
                .getDataAsync<T[]>(this.myDirectoryDataProviderContribution)
                .catch(error => {
                    this.showErrorMessage(error);
                    this.loadDirectoryDataPromise = undefined;
                    return undefined;
                })
                .then(data => processData(data));
        }
    };

    private loadAllDirectoryData = (): Promise<T[]> => {
        if (!this.loadAllDirectoryDataPromise) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

            this.loadAllDirectoryDataPromise = contributionService
                .getDataAsync<T[]>(this.allDirectoryDataProviderContribution)
                .catch(error => {
                    this.showErrorMessage(error);
                    this.loadAllDirectoryDataPromise = undefined;
                    return undefined;
                })
                .then((results: T[] | undefined) => {
                    if (results) {
                        this.allDirectoryData = results;
                        this.filteredAllDirectoryData.value = filterDirectories(this.allDirectoryData, this.filter.getState());
                    }
                    return this.allDirectoryData;
                });
        }

        return this.loadAllDirectoryDataPromise;
    };

    private updateIsFiltering = (value: IFilterState): void => {
        this.isFilteredObservable.value = hasFilter(value);
    }
}

function directoryCompare(a: DirectoryItem, b: DirectoryItem) {
    return a.name.localeCompare(b.name, navigator.language);
}

function filterDirectories<T extends DirectoryItem = DirectoryItem>(directories: T[] = [], filter?: IFilterState): T[] {
    if (!hasFilter(filter)) {
        return directories;
    }

    return directories.filter(
        (directory: T) =>
            (!filter.text || directory.name.toLocaleLowerCase().indexOf(filter.text.value.toLocaleLowerCase()) >= 0) &&
            (!filter.team || filter.team.value.length === 0 || filter.team.value.includes(directory.id))
    );
}

function hasFilter(filterState?: IFilterState): filterState is IFilterState {
    return (
        !!filterState &&
        Object.keys(filterState).some(key => {
            const value = filterState[key]?.value;
            return Array.isArray(value) ? value.length > 0 : !!value;
        })
    );
}

function createDirectoryDropdownItem(directory: DirectoryItem, group: string): IDirectoryDropdownItem {
    return {
        text: directory.name,
        id: `${directory.id}-${group}`,
        directory: directory,
        group,
        href: directory.url
    };
}
